import { Component, computed, inject, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { ChartService } from 'src/app/services/chart/chart.service';
import { ChartLimitGateService } from 'src/app/services/chart-limit-gate/chart-limit-gate.service';
import { Category } from 'src/app/types/category';
import { TableChart } from 'src/app/types/table-chart';
import { catchError, map, of, switchMap } from 'rxjs';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { featherChevronRight } from '@ng-icons/feather-icons';
import { Chart } from 'src/app/types/chart';
import {
  ChartMatchType,
  MatchingConditionType,
  MatchingData,
  MatchingOperator,
  MatchingProductConditon,
} from 'src/app/types/general';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { captureException } from '@sentry/angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { CtaDialogData, CtaDialogOutput } from '../cta-dialog/types';
import { ProductService } from 'src/app/services/product/product.service';
import { ShopifyProduct } from 'src/app/types/product';

@Component({
  templateUrl: './step-2.component.html',
  standalone: true,
  selector: 'app-onboarding-step-2',
  imports: [MatButtonModule, NgClass, NgIconComponent, TranslatePipe, MatDividerModule],
  viewProviders: [provideIcons({ featherChevronRight })],
  styles: `
    button[mat-button],
    button[mat-flat-button] {
      --mdc-filled-button-container-height: 36px;
      --mdc-text-button-container-height: 36px;
    }
  `,
})
export class Step2Component extends BaseComponent {
  private readonly chartService = inject(ChartService);
  private readonly chartLimitGate = inject(ChartLimitGateService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly productService = inject(ProductService);
  private readonly translate = inject(TranslateService);

  readonly back = output<void>();
  readonly continue = output<{
    nextStep: number;
    payload: { chart: TableChart | Chart; productId: string | undefined } | undefined;
  }>();

  readonly loading = signal(false);
  readonly selectedMethod = signal(-1);
  readonly optionsExpanded = signal(false);

  readonly continueDisabled = computed(() => this.selectedMethod() < 0 || this.selectedMethod() > 3);

  private _createTableChart() {
    this.loading.set(true);

    // Onboarding is never interrupted by the plan's published chart limit: past it, the first chart
    // is created as a draft instead, with a notice rather than the upgrade popup.
    this.sub$ = this.chartLimitGate
      .createPublishedOrDraft('table', (published) => this.chartService.createTableChart({ published }))
      .subscribe({
        next: ({ result: { chart }, keptAsDraft }) => {
          this.loading.set(false);
          if (keptAsDraft) this.notifySavedAsDraft();
          this.continue.emit({ nextStep: 4, payload: { chart, productId: undefined } });
        },
        error: (error) => {
          captureException(error);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            {
              panelClass: ['snackbar-error'],
            },
          );
          this.loading.set(false);
        },
      });
  }

  /** A notice, not a dialog: the chart exists as a draft and the merchant can publish it later. */
  private notifySavedAsDraft() {
    this.snackbar.open(
      this.translate.instant(_('chart-limit.onboarding-saved-as-draft')),
      this.translate.instant(_('common.close')),
    );
  }

  onContinue() {
    if (this.continueDisabled()) return;

    const method = this.selectedMethod();

    if (method === 0) this._createTableChart();
    if (method === 1) this.onCreateChartFromProduct();
    if (method === 2) this.continue.emit({ nextStep: 3, payload: undefined });
  }

  async onCreateChartFromProduct() {
    const Component = await import('./product-selector.component').then((m) => m.ProductSelectorComponent);

    this.loading.set(true);

    const dialogRef = this.dialog.open(Component);

    this.sub$ = dialogRef.afterClosed().subscribe((condition) => {
      if (!condition) {
        this.loading.set(false);
        return;
      }

      this.onSelectProduct(condition);
    });
  }

  async onSelectProduct(condition: MatchingProductConditon) {
    const Component = await import('src/app/components/cta-dialog/cta-dialog.component').then(
      (c) => c.CtaDialogComponent,
    );

    // GOO-372 — the chart created below is created *for* this product, so it should be born with
    // that product's variant structure. The selector hands back only a GID and a label, so the
    // product itself has to be fetched. A failed fetch opens the dialog unseeded rather than
    // failing the step: seeding is a convenience, and this is the middle of onboarding.
    const productId = condition.value.slice(condition.value.lastIndexOf('/') + 1);

    this.sub$ = this.productService
      .getProduct(productId)
      .pipe(
        map(({ product }) => product as ShopifyProduct | undefined),
        catchError((error) => {
          captureException(error);

          return of(undefined);
        }),
        switchMap((product) =>
          this.dialog
            .open<unknown, CtaDialogData, CtaDialogOutput>(Component, { data: { product } })
            .afterClosed(),
        ),
        switchMap((output) => {
          if (!output) return of(undefined);

          if (!output.chart) return of(undefined);

          const chart = output.chart as Chart;

          const matching: MatchingData = {
            matchType: ChartMatchType.any,
            conditions: [
              {
                type: MatchingConditionType.products,
                operation: MatchingOperator.exact,
                value: condition.value,
                item: condition.item,
              },
            ],
          };

          return this.chartService.updateChart(chart._id, {
            published: chart.published,
            matching,
            measurements: chart.measurements,
            guide: chart.guide,
            labels: chart.labels,
            model: chart.model,
            sizeRecommender: chart.sizeRecommender,
            optionNames: chart.optionNames,
            measurementVisibility: chart.measurementVisibility,
            showUnitConversion: chart.showUnitConversion,
            name: chart.name,
            category: (chart.category as Category)._id,
          });
        }),
      )
      .subscribe({
        next: (result) => {
          this.loading.set(false);

          if (!result) return;

          this.continue.emit({
            nextStep: 8,
            payload: { chart: result.chart, productId: condition.value.slice(condition.value.lastIndexOf('/') + 1) },
          });
        },
        error: (error) => {
          this.loading.set(false);
          captureException(error);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            {
              panelClass: ['snackbar-error'],
            },
          );
        },
      });
  }

  onExpandOptions() {
    this.optionsExpanded.set(!this.optionsExpanded());
  }
}
